import React, { useState, useEffect, useRef } from "react";
import RoleBasedLayout from "./RoleBasedLayout";
import Input from "../ui/Input";
import {
    useCreateCategory, useGetCategories, useUpdateCategoryItem, useGetPermissions, useUser,
    useReorderCategories, useDeleteCategory, useRenameCategory, useImportCategoryCSV,
} from "../Use-auth";
import { GripVertical, EllipsisVertical } from "lucide-react";
import { DragDropContext, Droppable, Draggable, } from "@hello-pangea/dnd";
import SucessToast from "../ui/SucessToast";
import EditableCell from "../ui/EditableCell";
import SkeletonPage from "../ui/SkeletonPage ";
import { api } from "../../lib/api";
import PermissionPopup from "../ui/PermissionPopup";
import Button from "../ui/Button";

export default function Category() {
    const [newCategory, setNewCategory] = useState("");
    const { data, refetch, isLoading, isFetching } = useGetCategories();
    const createCategory = useCreateCategory();
    const reorderCategories = useReorderCategories();
    const renameCategory = useRenameCategory();
    const deleteCategory = useDeleteCategory();
    const importCSV = useImportCategoryCSV();
    const updateItem = useUpdateCategoryItem();
    const { data: user } = useUser();
    const { data: existingPermissions, } = useGetPermissions();
    const [categoriesOrder, setCategoriesOrder] = useState([]);
    const [expandedCategoryId, setExpandedCategoryId] = useState(null);
    const [toast, setToast] = useState({ message: "", type: "" });
    const [openMenuId, setOpenMenuId] = useState(null);
    const [editingCategoryId, setEditingCategoryId] = useState(null);
    const [editedName, setEditedName] = useState("");
    const [importCategoryId, setImportCategoryId] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const menuRef = useRef(null);
    const fileInputRef = useRef(null);

    // hide Outside click //
    useEffect(() => {
        function handleClickOutside(event) {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setOpenMenuId(null);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const showToast = (message, type = "success") => {
        setToast({ message, type });
        setTimeout(() => setToast({ message: "", type: "" }), 3000);
    };

    const categories = Array.isArray(data) ? data : [];

    useEffect(() => {
        setCategoriesOrder(categories.sort((a, b) => a.order - b.order));
    }, [categories]);

    const handleSave = async () => {
        if (!newCategory.trim()) {
            return;
        }

        try {
            await createCategory.mutateAsync({
                categoryName: newCategory.trim(),
            });

            setNewCategory("");
            refetch();
            showToast("Category created successfully!", "success");
        } catch (err) {
            console.error("Error creating category:", err);
            showToast(err.message || "Failed to create category", "error");
        }
    };

    const toggleCategory = (id) => {
        setExpandedCategoryId(id);
    };

    const handleDragEnd = async (result) => {
        if (!result.destination) return;

        const newCategories = Array.from(categoriesOrder);
        const [moved] = newCategories.splice(result.source.index, 1);
        newCategories.splice(result.destination.index, 0, moved);

        setCategoriesOrder(newCategories);

        try {
            await reorderCategories.mutateAsync(newCategories.map((c) => c._id));
        } catch (err) {
            console.error("Failed to update category order:", err);
        }
    };

    const handleRemove = (id) => {
        const confirmDelete = window.confirm(
            "Are you sure you want to delete this category?"
        );
        if (!confirmDelete) return;

        deleteCategory.mutate(id, {
            onSuccess: () => {
                showToast("Category deleted successfully");
                setOpenMenuId(null);
            },
            onError: (err) => {
                showToast(err.message || "Delete failed", "error");
            },
        });
    };

    const startRename = (cat) => {
        setEditingCategoryId(cat._id);
        setEditedName(cat.categoryName);
        setOpenMenuId(null);
    };

    const saveRename = (catId) => {
        if (!editedName.trim()) {
            setEditingCategoryId(null);
            return;
        }

        renameCategory.mutate(
            { id: catId, categoryName: editedName.trim() },
            {
                onSuccess: () => {
                    setCategoriesOrder((prev) =>
                        prev.map((cat) =>
                            cat._id === catId
                                ? { ...cat, categoryName: editedName.trim() }
                                : cat
                        )
                    );

                    showToast("Category renamed successfully");
                    setEditingCategoryId(null);
                },
                onError: (err) => {
                    showToast(err.message || "Rename failed", "error");
                },
            }
        );
    };

    const handleImportClick = (catId) => {
        setImportCategoryId(catId);
        fileInputRef.current.click();
    };

    const handleCSVUpload = (e) => {
        const file = e.target.files[0];
        if (!file || !importCategoryId) return;

        importCSV.mutate(
            { id: importCategoryId, file },
            {
                onSuccess: (res) => {
                    // Filter out invalid items
                    const validItems = res.data?.items?.filter(item =>
                        Object.values(item).some(val => val && val !== "-" && val !== "")
                    );

                    setCategoriesOrder(prev =>
                        prev.map(cat =>
                            cat._id === importCategoryId
                                ? { ...cat, items: validItems }
                                : cat
                        )
                    );

                    showToast("CSV imported successfully");
                    setExpandedCategoryId(importCategoryId);
                    e.target.value = "";
                },
                onError: (err) => {
                    showToast(err.message || "CSV import failed", "error");
                },
            }
        );
    };

    useEffect(() => {
        if (categories.length > 0) {
            const sorted = [...categories].sort((a, b) => a.order - b.order)
                .map(cat => ({ ...cat, items: cat.items || [] }));
            setCategoriesOrder(sorted);

            if (!expandedCategoryId) {
                setExpandedCategoryId(sorted[0]._id);
            }
        }
    }, [categories]);

    const currentCategory = expandedCategoryId
        ? categoriesOrder.find(cat => cat._id === expandedCategoryId)
        : null;

    const isAdmin = user?.department === "Admin";
    const currentUserPermissions = isAdmin
        ? {
            management: {
                manager_view: true,
                manager_time: true,
            },
        }
        : existingPermissions?.find(p => p.userId === user?.userId);

    const canViewHome =
        isAdmin || currentUserPermissions?.management?.category_view === true;
    const canUpdate =
        isAdmin || currentUserPermissions?.management?.category_update === true;

    const dynamicColumns = currentCategory?.items?.length
        ? Object.keys(currentCategory.items[0]).filter(
            (key) => key !== "_id" && key !== "__v"
        )
        : [];

    const getDisplayValue = (value) => {
        if (Array.isArray(value)) return value.join(", ");
        if (typeof value === "object" && value !== null) {
            return Object.values(value).join(", ");
        }
        return value;
    };

    // const filteredItems = currentCategory?.items?.filter((item) =>
    //     dynamicColumns.some((key) => {
    //         const value = item[key];
    //         if (!value) return false;

    //         if (Array.isArray(value)) {
    //             return value.join(" ").toLowerCase().includes(searchTerm.toLowerCase());
    //         }

    //         return String(value).toLowerCase().includes(searchTerm.toLowerCase());
    //     })
    // );

    const allItems = currentCategory?.items || [];
    const filteredItems = searchTerm
        ? allItems.filter(item =>
            dynamicColumns.some((key) => {
                const value = item[key];
                if (!value) return false;
                if (Array.isArray(value)) return value.join(" ").toLowerCase().includes(searchTerm.toLowerCase());
                return String(value).toLowerCase().includes(searchTerm.toLowerCase());
            })
        )
        : allItems;

    const handleAddCustomItem = async (catId) => {
        const currentCat = categoriesOrder.find(cat => cat._id === catId);
        if (!currentCat) return;

        const columns = dynamicColumns.length ? dynamicColumns : ["name"];
        const newItem = {};
        columns.forEach(col => newItem[col] = "");

        const tempId = `temp-${Date.now()}`;
        const tempItem = { ...newItem, _id: tempId };

        setCategoriesOrder(prev =>
            prev.map(cat =>
                cat._id === catId
                    ? { ...cat, items: [...(cat.items || []), tempItem] }
                    : cat
            )
        );

        try {
            const endpoint = api.auth.category.addItem.path(catId);
            const method = api.auth.category.addItem.method;

            const response = await fetch(endpoint, {
                method,
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ newItem }),
                credentials: "include",
            });

            const data = await response.json();

            if (!response.ok || !data.success) throw new Error(data.message || "Failed to add item");

            setCategoriesOrder(prev =>
                prev.map(cat =>
                    cat._id === catId
                        ? {
                            ...cat,
                            items: cat.items.map(item =>
                                item._id === tempId ? data.data : item
                            ),
                        }
                        : cat
                )
            );

            showToast("Item added successfully");
        } catch (err) {
            console.error("Error adding item:", err);
            setCategoriesOrder(prev =>
                prev.map(cat =>
                    cat._id === catId
                        ? { ...cat, items: (cat.items || []).filter(item => item._id !== tempId) }
                        : cat
                )
            );
            showToast(err.message || "Failed to add item", "error");
        }
    };

    const handleUpdate = (catId, itemId, field, value) => {

        if (!itemId || String(itemId).startsWith("temp-")) {
            setCategoriesOrder(prev =>
                prev.map(cat =>
                    cat._id === catId
                        ? {
                            ...cat,
                            items: cat.items.map(item =>
                                item._id === itemId ? { ...item, [field]: value } : item
                            ),
                        }
                        : cat
                )
            );
            return;
        }

        setCategoriesOrder(prev =>
            prev.map(cat =>
                cat._id === catId
                    ? {
                        ...cat,
                        items: cat.items.map(item =>
                            item._id === itemId ? { ...item, [field]: value } : item
                        ),
                    }
                    : cat
            )
        );
        updateItem.mutate({ categoryId: catId, itemId, field, value }, {
            onSuccess: () => showToast("Updated successfully"),
            onError: err => showToast(err.message || "Update failed", "error"),
        });
    };

    if (isLoading) {
        return (
            <RoleBasedLayout>
                <SkeletonPage />
            </RoleBasedLayout>
        );
    }

    if (!canViewHome) {
        return (
            <RoleBasedLayout>
                <div className="relative h-[90.7vh] overflow-hidden">
                    <PermissionPopup />
                </div>
            </RoleBasedLayout>
        );
    }

    return (
        <RoleBasedLayout>
            <div className="flex flex-col md:flex-row h-[90vh]">
                {/* Sidebar */}
                <div className="w-full md:w-64 bg-gray-50 border-b md:border-b-0 md:border-r border-gray-200 overflow-y-auto p-2 sm:p-4">
                    <h3 className="text-[14px] sm:text-[16px] md:text-[20px] mb-2 sm:mb-4">Records</h3>

                    {canUpdate && (
                        <input
                            placeholder="Add new records"
                            value={newCategory}
                            onChange={(e) => setNewCategory(e.target.value)}
                            onBlur={handleSave}
                            onKeyDown={(e) => e.key === "Enter" && handleSave()}
                            className="mb-2 sm:mb-4 border p-2 w-full rounded text-[12px] sm:text-[14px]"
                        />
                    )}

                    <DragDropContext onDragEnd={handleDragEnd}>
                        <Droppable droppableId="categories">
                            {(provided) => (
                                <div {...provided.droppableProps} ref={provided.innerRef}>
                                    {categoriesOrder.map((cat, index) => (
                                        <Draggable key={cat._id} draggableId={cat._id} index={index}>
                                            {(provided) => (
                                                <div
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    className="mb-2 bg-white rounded shadow-sm relative"
                                                >
                                                    <div
                                                        onClick={() => toggleCategory(cat._id)}
                                                        className={`flex items-center justify-between cursor-pointer mt-2
                        ${expandedCategoryId === cat._id
                                                                ? "bg-blue-100 border-l-4 border-blue-500"
                                                                : "hover:bg-gray-100"
                                                            }`}
                                                    >
                                                        <div className="flex items-center gap-2 w-full p-2">
                                                            {canUpdate && (
                                                                <div {...provided.dragHandleProps} className="cursor-grab">
                                                                    <GripVertical size={16} color="grey" />
                                                                </div>
                                                            )}
                                                            {editingCategoryId === cat._id ? (
                                                                <Input
                                                                    value={editedName}
                                                                    autoFocus
                                                                    onChange={(e) => setEditedName(e.target.value)}
                                                                    onBlur={() => saveRename(cat._id)}
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === "Enter") saveRename(cat._id);
                                                                        if (e.key === "Escape") setEditingCategoryId(null);
                                                                    }}
                                                                    className="h-7 text-sm border px-1 rounded w-full"
                                                                />
                                                            ) : (
                                                                <span className="uppercase text-[11px] sm:text-[13px] cursor-pointer w-full">
                                                                    {cat.categoryName}
                                                                </span>
                                                            )}
                                                        </div>

                                                        {canUpdate && (
                                                            <div
                                                                className="cursor-pointer menu-btn"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setOpenMenuId(openMenuId === cat._id ? null : cat._id);
                                                                }}
                                                            >
                                                                <EllipsisVertical size={16} color="grey" />
                                                            </div>
                                                        )}

                                                        {openMenuId === cat._id && (
                                                            <div
                                                                ref={menuRef}
                                                                className="absolute right-2 top-10 z-50 w-40 bg-white border rounded shadow-md"
                                                            >
                                                                <input
                                                                    type="file"
                                                                    accept=".csv"
                                                                    ref={fileInputRef}
                                                                    className="hidden"
                                                                    onChange={handleCSVUpload}
                                                                />
                                                                <button
                                                                    className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        startRename(cat);
                                                                    }}
                                                                >
                                                                    Rename
                                                                </button>
                                                                <button
                                                                    className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm"
                                                                    onClick={() => handleRemove(cat._id)}
                                                                >
                                                                    Remove
                                                                </button>
                                                                <button
                                                                    className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm"
                                                                    onClick={() => handleImportClick(cat._id)}
                                                                >
                                                                    Import CSV
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </DragDropContext>
                </div>

                {/* Main Content */}
                <div className="flex-1 overflow-x-auto w-full">
                    {expandedCategoryId && currentCategory?.items?.length > 0 && (
                        <div className="p-3 border-b bg-white">
                            <Input
                                placeholder="Search in this records..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full md:max-w-sm"
                            />
                        </div>
                    )}

                    {expandedCategoryId ? (
                        !currentCategory || currentCategory.items?.length === 0 ? (
                            <div className="flex items-center justify-center h-full p-6">
                                <p className="text-gray-400">No data available</p>
                            </div>
                        ) : filteredItems.length === 0 ? (
                            <div className="flex items-center justify-center h-full p-6">
                                <p className="text-gray-400">No matching data found</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto w-full bg-gray-100 border">
                                {/* Header Row */}
                                <div
                                    className="grid bg-gray-100 border-b"
                                    style={{
                                        gridTemplateColumns: `80px repeat(${dynamicColumns.length}, minmax(120px, 1fr))`,
                                    }}
                                >
                                    <div className="p-2 text-center border-r text-[12px] sm:text-[14px]">S.NO</div>
                                    {dynamicColumns.map((col) => (
                                        <div key={col} className="p-2 border-r capitalize text-center truncate text-[12px] sm:text-[14px]">
                                            {col.replace(/([a-z])([A-Z])/g, "$1 $2")}
                                        </div>
                                    ))}
                                </div>

                                {/* Data Rows */}
                                {filteredItems.map((item, idx) => (
                                    <div
                                        key={item._id}
                                        className="grid hover:bg-gray-50"
                                        style={{
                                            gridTemplateColumns: `80px repeat(${dynamicColumns.length}, minmax(120px, 1fr))`,
                                        }}
                                    >
                                        <div className="p-2 text-center border-r text-[11px] sm:text-[13px]">{idx + 1}</div>
                                        {dynamicColumns.map((field) => (
                                            <EditableCell
                                                key={`${item._id}-${field}`}
                                                value={getDisplayValue(item[field])}
                                                onSave={(v) => {
                                                    const value = Array.isArray(item[field])
                                                        ? v.split(",").map((x) => x.trim()).filter(Boolean)
                                                        : v;
                                                    handleUpdate(expandedCategoryId, item._id, field, value);
                                                }}
                                                className="p-2 border-r break-words whitespace-normal text-center"
                                                canUpdate={canUpdate}
                                            />
                                        ))}
                                    </div>
                                ))}

                                {expandedCategoryId && (currentCategory?.items || []).length >= 0 && (
                                    <div className="p-3 flex justify-end bg-white border-t">
                                        <Button
                                            onClick={() => handleAddCustomItem(expandedCategoryId)}
                                            variant="success"
                                        >
                                            + Add Item
                                        </Button>
                                    </div>
                                )}
                            </div>
                        )
                    ) : (
                        <p className="text-gray-500 text-center mt-4 text-[12px] sm:text-[14px]">No records!</p>
                    )}
                </div>
            </div>
        </RoleBasedLayout>
    );
}